import type {
  AttributionSummaryResponse,
  BrandChecks,
  BrandHealth,
  BrandsResponse,
  CreatePromptRequest,
  DmarcSummaryResponse,
  EmailMetricsResponse,
  GiftRequest,
  LiveMetrics,
  LiveResponse,
  MailTriageListResponse,
  MailTriageRecord,
  PairWatchSnapshot,
  PluginDriftEvent,
  PluginDriftRecentResponse,
  RecentVisit,
  RecentVisitsResponse,
  SnapshotResponse,
  UpdatePromptRequest,
  VisitsStats,
} from "@voice2mail/shared/v2m_api";

export interface Prompt {
  id: string;
  name: string;
  description: string;
  type: string;
  version: number;
  schemaVersion: number;
  systemPrompt: string;
  userPromptTemplate: string;
  thinkingInstructions: string | null;
  status: string;
  alpha: number;
  beta: number;
  minTrafficShare: number;
  usageCount: number;
  likes: number;
  dislikes: number;
  stops: number;
  failures: number;
  totalTokensIn: number;
  totalTokensOut: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface PromptMetrics extends Prompt {
  satisfactionRate: number;
  avgTokensOut: number;
  confidenceBadge: "high" | "low";
}

// Request payloads reuse the generated wire models so their shape stays in
// sync with the backend contract. `thinkingInstructions` is an admin-only UI
// field the generated models do not carry (the backend ignores it), so the
// payloads extend the generated request with it.
export interface CreatePromptPayload extends CreatePromptRequest {
  thinkingInstructions?: string | null;
}

export interface UpdatePromptPayload extends UpdatePromptRequest {
  thinkingInstructions?: string | null;
}

// GET /v1/admin/models has no Pydantic response_model (backend returns raw doc.to_dict()),
// so this interface is a hand-typed shadow of ModelDoc (backend/voice2mail_proxy/src/db/models/
// model_config.py) with no compiler-enforced link to the real contract. It must NOT declare a
// field ModelDoc doesn't have — a prior `allowedTiers: string[]` here (never a real ModelDoc
// field) crashed ModelsPage.tsx against every real model (2026-07-21 PK finding: /models NIE
// DZIAŁA). The endpoint's zero OpenAPI type safety is the deeper root cause (openapi.json emits
// `schema: {}` for its 200 response) — a v2m-backend follow-up to add a real response_model and
// drop this shadow type entirely is warranted but out of scope for this crash fix.
export interface Model {
  id: string;
  provider: string;
  displayName: string;
  schemaVersion: number;
  isSuperGenerate: boolean;
  status: string;
  costPerMillionTokensIn: number;
  costPerMillionTokensOut: number;
  alpha: number;
  beta: number;
  usageCount: number;
  totalTokensIn: number;
  totalTokensOut: number;
  totalEstimatedCostUsd: number;
  totalLatencyMs: number;
  totalTimeToFirstTokenMs: number;
  failures: number;
  likes: number;
  dislikes: number;
  createdAt: string;
  updatedAt: string;
}

export interface ModelMetrics extends Model {
  avgLatencyMs: number;
  avgTtftMs: number;
  satisfactionRate: number;
}

export interface OverviewMetrics {
  totalGenerations: number;
  totalLikes: number;
  totalDislikes: number;
  satisfactionRate: number;
  failureRate: number;
  totalCostUsd: number;
  avgLatencyMs: number;
  totalTokensIn: number;
  totalTokensOut: number;
}

export interface Generation {
  id: string;
  userId: string;
  promptId: string;
  modelId: string;
  status: string;
  rating: string | null;
  createdAt: string;
}

// --- Analytics: time-series + per-request stats ---

export interface TimeseriesBucket {
  date: string;
  generations: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  avgCostPerEmail: number;
}

export interface TimeseriesResponse {
  days: number;
  buckets: TimeseriesBucket[];
}

export interface StatSummary {
  avg: number;
  min: number;
  max: number;
  p95: number;
}

export interface ExpensiveGeneration {
  id: string;
  userId: string;
  modelId: string;
  estimatedCostUsd: number;
  contextLength: number;
  inputLength: number;
  tokensIn: number;
  tokensOut: number;
  createdAt: string | null;
  status: string;
}

export interface PerRequestStats {
  days: number;
  sampleCount: number;
  cost: StatSummary;
  contextLength: StatSummary;
  tokensIn: StatSummary;
  topExpensive: ExpensiveGeneration[];
}

// --- Recording-duration distribution (V2M-DEV-012, M-B) ---
//
// p50/p90/p95/p99/max recording length in seconds, deliberately WITHOUT an
// `avg` field — see backend `compute_percentile_summary` docstring
// (src/utils/metrics/aggregation.py): a single outlier recording barely
// moves an average but fully dominates the tail this metric exists to show.

export interface RecordingDurationBucket {
  p50: number;
  p90: number;
  p95: number;
  p99: number;
  max: number;
  count: number;
}

export interface RecordingDurationResponse {
  days: number;
  overall: RecordingDurationBucket;
  free: RecordingDurationBucket;
  paid: RecordingDurationBucket;
}

// --- Recording-cap hit counter (V2M-DEV-012, M-C) ---
//
// Distinct-user + total-event counts for the hard-cap stop (the client-side
// "stopped by limit, not by user" event), split overall/free/paid the same
// way as recording-duration — see backend `get_recording_cap_hits` docstring
// (src/api/admin/metrics.py) for the full contract this mirrors.

// RecordingCapHitsBucket/RecordingCapHitsResponse USUNIĘTE (V2M-DEV-167,
// 2026-08-04): były ręczną kopią wygenerowanego kontraktu
// (`@voice2mail/shared/v2m_api`), o jedno pole uboższą. Gdy backend dołożył
// `hoursSinceLastEventEver`, ta kopia milczała — kompilator nie miał jak
// zauważyć, że sygnał jedzie na drucie bez konsumenta. Importuj typ
// wygenerowany, nigdy nie przepisuj go tutaj.

export interface UserDetails {
  uid: string;
  email: string;
  displayName: string;
  subscription: {
    tier: string;
    status: string;
  };
  quota: {
    used: number;
    limit: number;
    periodEnd: string;
  };
  feedbackStats: {
    totalRatings: number;
    pendingBonusRatings: number;
    bonusGenerations: number;
  };
  purchasedGenerations: number;
}

export interface UserSearchResult {
  uid: string;
  email: string;
  displayName: string;
  tier: string;
}

// Reuses the generated wire model { amount: number; type: string }; the UI
// constrains `type` to "bonus" | "purchased" at the call site.
export type GiftPayload = GiftRequest;

// --- Users list (paginated) ---

/**
 * Represents a row in the admin users list table.
 * Returned by GET /api/admin/users.
 */
export interface UserListItem {
  uid: string;
  email: string;
  tier: string;
  /** Account-creation timestamp (ISO 8601). Null for legacy docs without one. */
  registeredAt: string | null;
  /** Subscription cycle for paid tiers (e.g. "month"). Null for free tier. */
  billingInterval: string | null;
  generationCount: number;
}

export interface UserListResponse {
  users: UserListItem[];
  nextCursor?: string;
}

/**
 * A single billing-pipeline alert raised by the webhook-observability check.
 * Returned inside BillingObservability.alerts. Empty list = healthy.
 */
export interface BillingObservabilityAlert {
  /** Machine kind: dead_letter_growth | stale_webhook | processing_stuck. */
  kind: string;
  /** Human-readable description of what is wrong and what to check. */
  message: string;
  /** Structured supporting numbers (thresholds, counts, ages). */
  detail: Record<string, unknown>;
}

/**
 * Billing-pipeline health snapshot.
 * Returned by GET /api/admin/billing/observability and the POST refresh.
 */
export interface BillingObservability {
  /** ISO 8601 timestamp of when this check ran. */
  checkedAt: string;
  /** True when no alerts fired — show a green status. */
  healthy: boolean;
  /** Number of docs in the dead-letter queue. */
  deadLetterCount: number;
  /** Webhook events stuck in status="processing" past the TTL. */
  stuckProcessingCount: number;
  /** Hours since the most recent processed webhook; null if none ever. */
  newestEventAgeHours: number | null;
  /** Active alerts; empty = healthy. */
  alerts: BillingObservabilityAlert[];
}

// --- Attribution dashboard ---
//
// These reuse the generated wire models verbatim (strongly typed, unlike
// BillingObservability above which the backend returns as `any`), so there is
// nothing app-specific to add on top — just re-export under the app's type
// module for a single import surface across pages.
export type { AttributionSummaryResponse, RecentVisit, RecentVisitsResponse };

/**
 * `visits.by_utm_campaign` / `by_utm_content` / `by_landing_path` (material
 * attribution follow-up to `by_utm_source`), each a list of
 * `AcquisitionBucketCount` (`{key, count}`) — the same generated shape
 * `by_utm_source` already used, reused server-side for these three
 * dimensions too. The generated `VisitsStats` model declares them as
 * REQUIRED (a mid-session `npm run generate:api` by the parallel backend
 * session landed the real contract while this feature was being built —
 * see git history on `VisitsStats.ts`/`AcquisitionBucketCount.ts`).
 *
 * They stay OPTIONAL here on purpose: a regenerated client only proves the
 * *contract* shipped, not that the *deployed* backend this admin panel's
 * runtime environment switcher can point at (dev/prod, see `src/api/
 * client.ts`) has rolled out yet. Treat `undefined` (field absent — pre-
 * rollout backend) and `[]` (field present, window genuinely has no data)
 * as different states; render the corresponding table only when defined.
 * Safe to narrow back to the plain generated `VisitsStats` once the
 * rollout window has closed and this defensive gap no longer earns its
 * keep (see `BrandChecksWithApi` above for the same pattern's retirement).
 */
export interface VisitsStatsWithMaterialBreakdown extends Omit<
  VisitsStats,
  "byUtmCampaign" | "byUtmContent" | "byLandingPath"
> {
  byUtmCampaign?: VisitsStats["byUtmCampaign"];
  byUtmContent?: VisitsStats["byUtmContent"];
  byLandingPath?: VisitsStats["byLandingPath"];
}

// A plain `interface ... extends AttributionSummaryResponse` can't narrow
// `visits` back to optional-fields here — TS's `extends` rejects widening a
// required member to optional. Omit + intersect instead.
export type AttributionSummaryResponseWithMaterialBreakdown = Omit<
  AttributionSummaryResponse,
  "visits"
> & {
  visits: VisitsStatsWithMaterialBreakdown;
};

// --- Mail triage ---
//
// Same reasoning as Attribution above: the generated wire models are already
// strongly typed and camelCase, so re-export verbatim for a single import
// surface across pages.
export type { DmarcSummaryResponse, MailTriageListResponse, MailTriageRecord };

/** Urgency filter values accepted by GET /v1/admin/mail-triage. */
export type MailTriageUrgency = "urgent" | "normal" | "ignore";

/**
 * Known `category` values from the triage engine's MailCategory enum.
 *
 * The wire type keeps `category` as a plain string (the triage engine is a
 * separate repo/deploy and can add categories without a lockstep proxy
 * redeploy) — this list is only the admin panel's filter dropdown options,
 * not a contract the backend enforces.
 */
export const MAIL_TRIAGE_CATEGORIES = [
  "v2m_canary_alert",
  "dmarc_report",
  "stripe_action_required",
  "stripe_security_info",
  "google_security_alert",
  "saas_transient",
  "human",
  "unknown",
] as const;

// --- Campaigns (migrated from voice2mail_webpage's orphaned /admin/campaigns) ---
//
// V2M-DEV-724 (2026-09-02): all 8 campaign/quota RESPONSE shapes now come
// from the generated client (`CampaignResponse`, `CampaignListResponse`,
// `SendTestResponse`, `TestResult`, `DispatchCampaignResponse`,
// `CancelCampaignResponse`, `DeliveriesResponse`, `DeliveryRecord`,
// `EmailQuotaStatus` — see client.ts's imports from
// `@voice2mail/shared/v2m_api`), camelCase per the generator's
// `modelPropertyNaming=camelCase`. Only the REQUEST-side shape below stays
// local: `createCampaign()` in client.ts accepts this snake_case contract
// (this panel's own long-standing convention for wizard input) and
// translates it into the generated camelCase request body itself.

export interface AudienceFilter {
  plan_tier?: string[];
  registered_before?: string;
  registered_after?: string;
}

export interface CreateCampaignRequest {
  template: string;
  /**
   * `<id>@<revision>` idempotency coordinate for this campaign (e.g.
   * `tos@2026-08-09`) — required by the backend
   * (`src.services.email_campaign.EmailCampaignService.create_campaign`)
   * since V2M-DEV-311/355: calling `POST /admin/campaigns` again with the
   * SAME `(template, document_version)` reuses the existing campaign and
   * only queues newly-eligible recipients, instead of creating a duplicate.
   */
  document_version: string;
  params: Record<string, unknown>;
  audience_filter?: AudienceFilter | null;
}

// --- Brand Health (Vigil) ---
//
// Absorbed from the standalone `products/x2mail-admin` SPA (AgiEu repo) per
// PK's single-panel consolidation directive (2026-07-15): the Vigil
// observability dashboard (pair-watch decision grid, live traffic, brand DNS/
// cert/mail health) becomes a section of this panel instead of a separate
// deploy. GET /v1/vigil/* is served by the same voice2mail_proxy backend this
// panel already targets (VigilApi sits alongside AdminApi in the generated
// client) — same base URL, same admin gate, no separate host.
//
// These reuse the generated wire models verbatim (strongly typed, camelCase),
// same reasoning as Attribution/MailTriage above — re-export under this
// module for a single import surface across the Brand Health pages.
export type {
  BrandChecks,
  BrandHealth,
  BrandsResponse,
  EmailMetricsResponse,
  LiveMetrics,
  LiveResponse,
  PairWatchSnapshot,
  SnapshotResponse,
};

/**
 * `BrandChecks` (2026-07-25, dyktuj2mail prod-promotion incident —
 * `dyktuj2mail-web.web.app` served HTTP 200 with a healthy-looking page whose
 * bundle silently called `api-dev.x2mail.eu` instead of prod). The producer
 * (`status_page_probe.py`, AgiEu `infra/hosts/charon/jobs/`) writes this key
 * to `brand_health/latest.brands[].checks.api` — verdict for "does the
 * brand's served frontend bundle point at the production API host, not dev".
 *
 * The v2m-backend follow-up this comment used to track HAS LANDED (`api: str
 * | None = None` on `BrandChecks` in `backend/voice2mail_proxy/src/api/
 * vigil.py`, commit `30745a21`) — the generated `BrandChecks` wire type
 * already carries `api?: string | null` natively. This alias is now a plain
 * passthrough, kept only so `BrandsView.tsx`'s existing `BrandChecksWithApi`
 * references don't need a rename; a manual `& { api?: string }` intersection
 * here would actively CONFLICT with the generated (nullable) field and fail
 * to compile — that conflict is exactly what broke when the backend field
 * landed and this stale extension wasn't retired alongside it.
 */
export type BrandChecksWithApi = BrandChecks;

/**
 * Narrow UI-only union over `PairWatchSnapshot.verdict` (the wire type keeps
 * it a plain `string` so the backend never 500s on a verdict value the panel
 * hasn't been updated to recognise yet — see `PairWatchSnapshot` in
 * `backend/voice2mail_proxy/src/api/vigil.py`). Exhaustive switches in the
 * Brand Health views narrow to this type at the render boundary.
 */
export type PairVerdict = "OK" | "ALERT_LOW" | "ALERT_HIGH" | "BELOW_VOLUME" | "NO_BASELINE";

// --- Plugin selector-drift telemetry ---
//
// GET /v1/admin/plugin-drift/recent surfaces the plugin_reactive_fetches
// Firestore collection (backend/voice2mail_proxy/src/services/
// plugin_reactive_fetch_service.py) for the admin panel: every allowed
// reactive-fetch call means a bundled DOM selector just failed for a real
// user, so this is the early-warning signal for Gmail/Outlook adapter drift.
// Re-exported verbatim (already camelCase), same reasoning as the sections
// above.
export type { PluginDriftEvent, PluginDriftRecentResponse };
